var _react_refresh_temp_1;
var _react_refresh_temp_2;
_react_refresh_temp_2 = $RefreshSig$();
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { useNavigate } from 'react-router-dom';
import { BadRequestError, HttpClientError, TooManyRequestsError, } from '@lucidlink/sdk/fetcher';
import { assertCanHandle } from '@lucidlink/tscore';
import { Box, Button, deriveOtherFieldValue, excludeOtherFieldValue, LISTBOX_OTHER_OPTION_ID, MessageBox, Strong, Trans, translate, Typography, useModal, useToastService, WordBreak, } from '@lucidlink/uicore';
import { ConfirmDelete, VerifyCodeModal } from '#components';
import { DELETE_STEPS, PII_MASK_CLASS } from '#constants';
import { useLucidSdk } from '#context';
import { useDeploymentGuard } from '#hooks';
import { workspaceRoutes } from '#services';
import { createWsFsDeleteEventPayload } from '#telemetry';
import { useFilespaceDelete } from '../../../../query-hooks/mutations/useFilespaceDelete';
import { useFilespaceDeleteVerificationCode } from '../../../../query-hooks/mutations/useFilespaceDeleteVerificationCode';
const getListBoxOptions = () => [
    { id: 'project-completed', text: translate('Project completed') },
    { id: 'data-reorganization', text: translate('Data reorganization') },
    { id: 'no-longer-needed', text: translate('No longer needed') },
    { id: 'moving', text: translate('Moving to a different solution') },
    { id: LISTBOX_OTHER_OPTION_ID, text: translate('Other') },
];
const CONFIRM_DELETE_MODAL_ID = 'ConfirmDelete';
export const FilespaceDelete = ({ currentUser, filespace, }) => {
    _react_refresh_temp_2();
    const { lucidSdk } = useLucidSdk();
    const userEmail = currentUser?.email ?? translate('your email');
    const { name: filespaceName, workspaceId } = filespace;
    const [deleteStep, setDeleteStep] = useState(DELETE_STEPS.ConfirmDelete);
    const [deletionReasonsData, setDeletionReasonsData] = useState();
    const [sendCodeError, setSendCodeError] = useState(null);
    const clearSendCodeError = useCallback(() => setSendCodeError(null), []);
    const [verificationCodeError, setVerificationCodeError] = useState();
    const [verificationRateLimitError, setVerificationRateLimitError] = useState(null);
    const clearVerificationRateLimitError = useCallback(() => setVerificationRateLimitError(null), []);
    const navigate = useNavigate();
    const { guard } = useDeploymentGuard();
    const guardDelete = useCallback((action) => {
        void guard(action, { workspaceId });
    }, [guard, workspaceId]);
    const isSubmittingRef = useRef(false);
    const submitOnce = useCallback((submit) => {
        if (isSubmittingRef.current) {
            return;
        }
        isSubmittingRef.current = true;
        submit();
    }, []);
    const { addToast } = useToastService();
    const { closeModal, openModal, updateModal } = useModal({
        modalId: CONFIRM_DELETE_MODAL_ID,
        closeOnUnmount: true,
    });
    const listBoxOptions = useMemo(() => getListBoxOptions(), []);
    const onCancel = useCallback(() => {
        setDeleteStep(DELETE_STEPS.ConfirmDelete);
        closeModal();
        setVerificationCodeError(null);
        setVerificationRateLimitError(null);
    }, [closeModal]);
    const onSuccessSendCode = useCallback(() => {
        isSubmittingRef.current = false;
        addToast({
            'data-testid': 'filespace-delete-send-code-success',
            content: {
                message: translate('A confirmation code has been sent to your email.'),
            },
            kind: 'positive',
        });
        setDeleteStep(DELETE_STEPS.ConfirmCode);
    }, [addToast]);
    // handle modal error state update
    const errorCallbackRef = useRef(null);
    const onErrorSendCode = (error) => {
        isSubmittingRef.current = false;
        assertCanHandle(error, (err) => err instanceof HttpClientError ||
            err instanceof TooManyRequestsError);
        errorCallbackRef.current?.(error);
        setSendCodeError(error);
    };
    const { mutation: { isPending: isPendingFilespaceDeleteCode, mutate: sendDeleteCode, }, } = useFilespaceDeleteVerificationCode({
        onSuccess: onSuccessSendCode,
        onError: onErrorSendCode,
    });
    const onSuccessDeleteFilespace = useCallback(() => {
        void navigate(workspaceRoutes.workspace(workspaceId));
        addToast({
            kind: 'positive',
            content: {
                message: translate('You successfully deleted the {{filespaceName}} filespace.', { filespaceName }),
            },
            'data-testid': 'filespace-deleted',
        });
        if (deletionReasonsData) {
            const otherFieldValue = deriveOtherFieldValue({
                listBoxValue: deletionReasonsData,
                options: listBoxOptions,
            });
            lucidSdk.telemetryClient().sendEvent(createWsFsDeleteEventPayload({
                workspaceId,
                filespaceId: filespace.id,
                deletionReasons: excludeOtherFieldValue({
                    listBoxValue: deletionReasonsData,
                    options: listBoxOptions,
                }),
                ...(otherFieldValue?.length && {
                    otherDeletionReason: otherFieldValue,
                }),
            }));
        }
    }, [
        addToast,
        deletionReasonsData,
        filespace.id,
        filespaceName,
        listBoxOptions,
        lucidSdk,
        navigate,
        workspaceId,
    ]);
    const onErrorDeleteFilespace = (error) => {
        isSubmittingRef.current = false;
        if (error instanceof TooManyRequestsError) {
            setVerificationRateLimitError(error);
            return;
        }
        addToast({
            kind: 'negative',
            content: {
                message: translate('Deleting the {{filespaceName}} filespace has failed.', { filespaceName }),
            },
            'data-testid': 'filespace-deleted-fail',
        });
        assertCanHandle(error, (err) => err instanceof BadRequestError);
        setVerificationCodeError(error.errors[0]);
    };
    const { mutation: { isPending: isPendingDeleteFilespace, mutate: deleteFilespace, }, } = useFilespaceDelete({
        onSuccess: onSuccessDeleteFilespace,
        onError: onErrorDeleteFilespace,
    });
    const onVerifyCode = useCallback((code) => guardDelete(() => submitOnce(() => deleteFilespace({ filespaceData: filespace, code }))), [guardDelete, submitOnce, deleteFilespace, filespace]);
    const sendCode = useCallback(() => {
        guardDelete(() => submitOnce(() => {
            setSendCodeError(null);
            sendDeleteCode({
                filespaceId: filespace.id,
                workspaceId: filespace.workspaceId,
            });
        }));
    }, [
        guardDelete,
        submitOnce,
        filespace.id,
        filespace.workspaceId,
        sendDeleteCode,
    ]);
    const openConfirmDeleteModal = useCallback(() => openModal({
        content: (_jsx(ConfirmDelete, { entityName: filespace.name, entityType: translate('filespace'), information: _jsx(Trans, { children: "Deleting this filespace will delete all stored data. This cannot be undone." }), isCodePending: isPendingFilespaceDeleteCode, onCancel: onCancel, onConfirm: sendCode, onSendCodeErrorCountDown: clearSendCodeError, propagateDeletionReasons: setDeletionReasonsData, reasonForDeletionOptions: listBoxOptions, registerErrorCallback: (callback) => {
                errorCallbackRef.current = callback;
            }, sendCodeError: sendCodeError })),
    }), [
        clearSendCodeError,
        filespace,
        isPendingFilespaceDeleteCode,
        listBoxOptions,
        onCancel,
        openModal,
        sendCode,
        sendCodeError,
    ]);
    const openConfirmCodeModal = useCallback(() => {
        updateModal({
            content: (_jsx(VerifyCodeModal, { closeModal: onCancel, descriptionContent: _jsxs(Trans, { shouldUnescape: true, values: { userEmail }, children: ["We've sent a confirmation code to", ' ', _jsx(WordBreak, { className: PII_MASK_CLASS, children: '{{userEmail}}' }), "."] }), isOperationRunning: isPendingDeleteFilespace, isSendCodeRunning: isPendingFilespaceDeleteCode, onSendCodeErrorCountDown: clearSendCodeError, onVerificationRateLimitCountDown: clearVerificationRateLimitError, operation: onVerifyCode, operationName: translate('delete'), sendCode: sendCode, sendCodeError: sendCodeError, titleContent: _jsxs(Trans, { shouldUnescape: true, values: { filespaceName }, children: ["Enter confirmation code to delete the", _jsx("br", {}), _jsx(Strong, { className: PII_MASK_CLASS, wordBreak: "breakWord", children: '{{filespaceName}}' }), ' ', "filespace"] }), verificationCodeError: verificationCodeError, verificationRateLimitError: verificationRateLimitError })),
        });
    }, [
        clearSendCodeError,
        clearVerificationRateLimitError,
        onCancel,
        filespaceName,
        isPendingDeleteFilespace,
        isPendingFilespaceDeleteCode,
        onVerifyCode,
        sendCode,
        sendCodeError,
        updateModal,
        userEmail,
        verificationCodeError,
        verificationRateLimitError,
    ]);
    useEffect(() => {
        if (deleteStep === DELETE_STEPS.ConfirmCode) {
            openConfirmCodeModal();
        }
    }, [deleteStep, openConfirmDeleteModal, openConfirmCodeModal]);
    return (_jsx(_Fragment, { children: _jsxs(Box, { align: "start", gap: "l", orientation: "vertical", children: [_jsxs(Box, { gap: "s", orientation: "vertical", children: [_jsx(Typography, { as: "h3", color: "accent", "data-testid": "delete-filespace-header", kind: "h5", children: _jsx(Trans, { children: "Delete filespace" }) }), _jsx(Typography, { "data-testid": "delete-filespace-info-1", kind: "p-small", children: _jsx(Trans, { children: "Once this filespace is deleted, all stored files will be permanently removed, and neither you nor your team will be able to access or recover them." }) })] }), _jsx(MessageBox, { "data-testid": "delete-filespace-info-2", type: "alert", children: filespace.canDelete ? (_jsx(Trans, { children: "Please note that this action is irreversible, and all data stored in this filespace will be lost." })) : (_jsx(Trans, { children: "To delete this Filespace, there shouldn\u2019t be any errors or operations in progress." })) }), _jsx(Button, { "data-testid": "delete-filespace", disabled: !filespace.canDelete, kind: "danger", onClick: () => guardDelete(openConfirmDeleteModal), size: "big", children: _jsx(Trans, { children: "Delete filespace" }) })] }) }));
};
_react_refresh_temp_1 = FilespaceDelete;
$RefreshReg$(_react_refresh_temp_1, "FilespaceDelete");
_react_refresh_temp_2(FilespaceDelete, "8Oyj078PJm5mYdf2whIGOsxJwFg=", false, () => [useLucidSdk, useNavigate, useDeploymentGuard, useToastService, useModal, useFilespaceDeleteVerificationCode, useFilespaceDelete]);
